﻿import React from "react";
import { useSendAuditDialog } from "./SendAuditDialog.hooks";
import { arrow, leftWhiteArrowButton } from "../../../common/icons";
import { DotLegalDatePicker, DotLegalDialog, DotLegalMultiSelect, DotLegalSelect, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { apiUrls } from "../../../common/api/apiUrls";
import DotLegalSelectApi from "../../../common/components/dotLegalSelectApi/DotLegalSelectApi";
import DotLegalMultiSelectApi from "../../../common/components/dotLegalMultiSelectApi/DotLegalMultiSelectApi";
import { Box, TableCell, TableRow } from "@mui/material";
import { LegalEntityContactRow } from "../../legalEntityContactTab/LegalEntityContactTab.types";
import { formatContactOptionName } from "../auditAdditionalRecipients.utils";
import { QuestionnaireSelectableItem } from "../LegalEntityAudit.types";
import { SendAuditViewModel } from "./SendAuditDialog.types";
import GroupEntityStep from "../groupEntityStep/GroupEntityStep";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { PlanLimit } from "../../../auth/planProvider/PlanProvider.types.ts";
import TrialUsageWarning from "../../../common/components/TrialUsageWarning.tsx";
import AuditTemplateLinkWrapper from "../../../common/AuditTemplateLinkWrapper.tsx";

export interface ISendAuditDialogProps {
    onDialogClose: () => void;
    onAuditSent: () => void;
    legalEntityId: string | undefined;
    legalEntityName: string;
    responseId?: string;
    questionnaire?: QuestionnaireSelectableItem;
    planLimitData: PlanLimit | undefined;
}

function SendAuditDialog(props: ISendAuditDialogProps) {
    const { translateString, getDateTimePickerLanguage, translateDate } = useTranslation();
    const { permissions } = useUserContext();

    const {
        onUpdate,
        model,
        onGroupEntityChanged,
        onNextOrSendClick,
        onPreviousButtonClick,
        step,
        isSending,
        selectableGroupEntitiesLoading,
        selectableGroupEntitiesData,
        validation,
        selectableResponsiblesData,
        selectableresponsiblesLoading,
        additionalRecipientOptions,
        additionalRecipientNames,
        isLoadingContacts,
    } = useSendAuditDialog(props);

    const stepSelect = (
        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
            <Box sx={{ width: 450 }}>
                <AuditTemplateLinkWrapper>
                    <DotLegalSelectApi<QuestionnaireSelectableItem>
                        url={apiUrls().questionnaires.selectableItems}
                        onChange={onUpdate.onAuditChanged}
                        label={translateString("template")}
                        noOptionsLabel={translateString("noOptions")}
                        selectedItem={model.questionnaireId}
                        toolTipText={translateString("selectAuditInfo")}
                        errorText={validation.getErrorsForDisplay("questionnaireId")}
                        disabled={props.questionnaire !== undefined}
                        disableClearable
                    />
                </AuditTemplateLinkWrapper>

                <DotLegalSelectApi<LegalEntityContactRow>
                    url={apiUrls().legalEntities.contacts(props.legalEntityId!)}
                    onChange={onUpdate.onContactChanged}
                    label={translateString("contact")}
                    noOptionsLabel={translateString("noOptions")}
                    selectedItem={model.contactId}
                    toolTipText={translateString("selectAuditContactInfo")}
                    errorText={validation.getErrorsForDisplay("contactId")}
                    onOptionsRender={(item) => formatContactOptionName(item, translateString)}
                />

                <DotLegalMultiSelect
                    options={additionalRecipientOptions}
                    chosenOptions={model.additionalRecipientIds}
                    onChange={onUpdate.onAdditionalRecipientsChanged}
                    label={translateString("additionalRecipients")}
                    noOptionsLabel={translateString("noOptions")}
                    toolTipText={translateString("selectAuditAdditionalRecipientsInfo")}
                    isLoading={isLoadingContacts}
                />

                <DotLegalSelect
                    options={selectableResponsiblesData}
                    onChange={onUpdate.onResponsibleChanged}
                    label={translateString("responsible")}
                    selectedItem={model.responsibleId}
                    noOptionsLabel={translateString("noOptions")}
                    isLoading={selectableresponsiblesLoading}
                    errorText={validation.getErrorsForDisplay("responsibleId")}
                    toolTipText={translateString("legalEntityAuditReponsibleTooltip")}
                />

                <DotLegalDatePicker
                    label={translateString("deadline")}
                    value={model.deadline ?? null}
                    onChange={onUpdate.onDateChanged}
                    language={getDateTimePickerLanguage()}
                    toolTipText={translateString("selectAuditDeadlineInfo")}
                    error={!!validation.getErrorsForDisplay("deadline")}
                    helperMessage={validation.getErrorsForDisplay("deadline")}
                />

                {permissions.systemPermissions.read && (
                    <DotLegalMultiSelectApi
                        url={apiUrls().systems.selectableItemsByLegalEntityId(props.legalEntityId!)}
                        onChange={onUpdate.onSystemsChanged}
                        label={translateString("systems")}
                        noOptionsLabel={translateString("noOptions")}
                        chosenOptions={model.systemIds}
                        toolTipText={translateString("selectAuditSystemsInfo")}
                        disabled={model.processingActivities.length > 0}
                    />
                )}

                {permissions.processingActivityPermissions.read && (
                    <DotLegalMultiSelectApi
                        url={apiUrls().processingActivites.selectableItemsByAccess}
                        onChange={onUpdate.onProcessingActivityChange}
                        label={translateString("processingActivities")}
                        noOptionsLabel={translateString("noOptions")}
                        chosenOptions={model.processingActivities.map((x) => x.id)}
                        toolTipText={translateString("selectAuditProcessingActivityInfo")}
                        disabled={model.systemIds.length > 0}
                    />
                )}
            </Box>
        </Box>
    );

    const stepGroupCompany = (
        <GroupEntityStep
            showGdpoIfAvailable
            onGroupEntityChanged={onGroupEntityChanged}
            errormsg={validation.getErrorsForDisplay("groupEntityId")}
            groupEntityId={model.groupEntityId}
            selectableGroupEntitiesData={selectableGroupEntitiesData}
            header={translateString("legalEntityAudtiGroupEntityHeader")}
            infoTranslationKey={"legalEntityAudtiGroupEntity"}
            checkboxTranslationKey={"legalEntityAuditEntireGroup"}
            checkboxTooltip={translateString("legalEntityAuditEntireGroupInfo")}
        />
    );

    function getHeaders() {
        let headers: Array<ITableHeader<SendAuditViewModel>> = [
            { property: "legalEntityName", text: translateString("legalEntity"), align: "left", showOnMobile: true },
            { property: "contactName", text: translateString("contact"), align: "left", showOnMobile: true },
            { property: "contactEmail", text: translateString("contactEmail"), align: "left", showOnMobile: true },
            { property: "additionalRecipientIds", text: translateString("additionalRecipients"), align: "left", showOnMobile: true },
            { property: "responsibleId", text: translateString("responsible"), align: "left", showOnMobile: true },
            { property: "responsibleId", text: translateString("responsibleEmail"), align: "left", showOnMobile: true },
        ];

        if (model.systemIds.length > 0) {
            headers.push({ property: "systemNames", text: translateString("systems"), align: "left", showOnMobile: true });
        } else if (model.processingActivities.length > 0) {
            headers.push({ property: "processingActivities", text: translateString("processingActivities"), align: "left", showOnMobile: true });
        }

        headers.push({ property: "questionnaireName", text: translateString("template"), align: "left", showOnMobile: true });
        headers.push({ property: "deadline", text: translateString("deadline"), align: "left", showOnMobile: true });
        headers.push({ property: "groupEntityName", text: translateString("sentFrom"), align: "left", showOnMobile: true });

        return headers;
    }

    const stepSend = (
        <div>
            <DotLegalTable
                headers={getHeaders()}
                defaultOrderBy={"legalEntityName"}
                defaultOrder={"asc"}
                isLoading={false}
                clickableRows={false}
                hidePagination
                data={[model]}
                renderRow={(row, i) => {
                    const responsible = selectableResponsiblesData?.find((x) => x.id === row.responsibleId);
                    return (
                        <TableRow key={row.legalEntityName! + i}>
                            <TableCell>{row.legalEntityName}</TableCell>
                            <TableCell>{row.contactName}</TableCell>
                            <TableCell>{row.contactEmail}</TableCell>
                            <TableCell>{additionalRecipientNames}</TableCell>
                            <TableCell>{responsible?.name ?? ""}</TableCell>
                            <TableCell>{responsible?.email ?? ""}</TableCell>
                            {(row.systemNames.length > 0 || row.processingActivities.length > 0) && (
                                <TableCell>
                                    {row.systemNames.length > 0 ? row.systemNames.join(", ") : row.processingActivities.map((x) => x.name).join(", ")}
                                </TableCell>
                            )}
                            <TableCell>{row.questionnaireName}</TableCell>
                            <TableCell>{translateDate(row.deadline!)}</TableCell>
                            <TableCell>{row.groupEntityName}</TableCell>
                        </TableRow>
                    );
                }}
                paginationLabelOf={""}
            />
        </div>
    );

    return (
        <DotLegalDialog
            header={translateString("sendAudit")}
            size={"lg"}
            buttonOkIcon={arrow}
            buttonOkText={step === "send" ? translateString("send") : translateString("next")}
            onDialogClose={props.onDialogClose}
            onOkClick={onNextOrSendClick}
            previousButtonText={translateString("previous")}
            previousButtonClick={step !== "first" ? onPreviousButtonClick : undefined}
            previousButtonIcon={leftWhiteArrowButton}
            open
            buttonRight
            inProgress={isSending}
            okButtonDisabled={selectableGroupEntitiesLoading}
        >
            {step === "first" ? stepSelect : step === "selectGroupEntity" ? stepGroupCompany : stepSend}
            {props.planLimitData?.warnLimitReachedOnTrial && (
                <TrialUsageWarning marginTop={2} textKey={translateString("createExceedingNumberOfExternalAuditsWarning")} />
            )}
        </DotLegalDialog>
    );
}

export default SendAuditDialog;
